import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  MenuItem,
  FormControl,
  Select,
  Button,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Autocomplete,
  TextField,
  IconButton,
  Checkbox,
  ListItemText,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridRowModel,
  type GridRenderEditCellParams,
  type GridCellParams,
  useGridApiContext,
  useGridApiRef,
} from '@mui/x-data-grid';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from 'store/hook';
import { showNotification } from 'store/notification.slice';
import { selectAssets } from 'store/asset.slice';
import useAppTheme from 'hooks/useAppTheme';
import { useRoleBasedAccess } from 'hooks/useRoleBasedAccess';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import ReportAPI from 'api/report.api';
import LocationImporterAPI from 'api/location-importer.api';
import AddEquipmentStatusRowModal, {
  type NewRowPayload,
} from './AddEquipmentStatusRowModal';

dayjs.extend(utc);
dayjs.extend(timezone);

const EDITABLE_ROLES = [
  'director',
  'internal-user',
  'external-superadmin',
  'external-admin',
];

// Predefined time-category values — used both for the editable grid column
// and the Time Category filter dropdown.
const TIME_CATEGORY_OPTIONS = [
  'Ready',
  'Delay Time',
  'Secondary Non Production Time',
  'Operational Stops Time',
  'Scheduled Down Time',
  'Unscheduled Down Time',
  'Standby',
];

// Editable Time / Time End cell. Shows the formatted value with a pencil icon;
// clicking it opens a date + time picker. On accept it validates the ordering
// (start must not be after end, end must not be before start) and patches.
const EditableTimeCell = ({
  value,
  editable,
  onSave,
  minDateTime,
  maxDateTime,
  tz,
}: {
  value: string | null | undefined;
  editable: boolean;
  onSave: (iso: string) => Promise<void> | void;
  minDateTime?: Dayjs;
  maxDateTime?: Dayjs;
  tz: string;
}) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // Draft the picker edits — seeded with the current cell value each time it opens
  const [draft, setDraft] = useState<Dayjs | null>(null);
  const display = value ? dayjs(value).tz(tz).format('DD/MM/YY HH:mm') : '';

  return (
    <Box
      ref={anchorRef}
      sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 0.5 }}
    >
      {editable && (
        <IconButton
          size="small"
          disabled={saving}
          onClick={() => {
            setDraft(value ? dayjs(value).tz(tz) : null);
            setOpen(true);
          }}
          sx={{ p: 0.25, flexShrink: 0 }}
        >
          {saving ? (
            <CircularProgress size={13} />
          ) : (
            <EditIcon sx={{ fontSize: 13, opacity: 0.5 }} />
          )}
        </IconButton>
      )}
      <span
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {display}
      </span>
      {editable && (
        <DateTimePicker
          open={open}
          onClose={() => {
            setOpen(false);
          }}
          value={draft}
          onChange={(v) => {
            setDraft(v);
          }}
          timezone={tz}
          minDateTime={minDateTime}
          maxDateTime={maxDateTime}
          ampm={false}
          minutesStep={1}
          // Controls the rendered minutes list in the digital clock (v6);
          // minutesStep alone only affects validation, not the list step.
          timeSteps={{ hours: 1, minutes: 1 }}
          format="DD/MM/YY HH:mm"
          onAccept={async (v) => {
            setOpen(false);
            if (!v) return;
            setSaving(true);
            try {
              await onSave(v.utc().toISOString());
            } finally {
              setSaving(false);
            }
          }}
          slotProps={{
            textField: { sx: { display: 'none' } },
            // Anchor the popup to the cell and keep it inside the viewport
            popper: {
              anchorEl: anchorRef.current,
              placement: 'bottom-start',
              modifiers: [
                { name: 'flip', enabled: true },
                {
                  name: 'preventOverflow',
                  enabled: true,
                  options: { altAxis: true, padding: 8 },
                },
              ],
            },
          }}
        />
      )}
    </Box>
  );
};

// Generic dropdown edit cell — commits the selected value and closes edit mode.
const DropdownEditCell = ({
  id,
  field,
  value,
  options,
  allowNone = true,
}: GridRenderEditCellParams & { options: string[]; allowNone?: boolean }) => {
  const apiRef = useGridApiContext();
  // Open the menu automatically as soon as edit mode begins, so a single click
  // on the cell opens the dropdown instead of requiring a second click.
  const [open, setOpen] = useState(true);
  return (
    <Select
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
        // Closing without picking anything should exit edit mode too
        apiRef.current.stopCellEditMode({ id, field });
      }}
      value={value ?? ''}
      onChange={(e) => {
        void apiRef.current.setEditCellValue({
          id,
          field,
          value: e.target.value,
        });
        apiRef.current.stopCellEditMode({ id, field });
      }}
      size="small"
      displayEmpty
      fullWidth
      sx={{ fontSize: '0.875rem' }}
    >
      {allowNone && (
        <MenuItem value="">
          <em>None</em>
        </MenuItem>
      )}
      {options.map((opt) => (
        <MenuItem key={opt} value={opt}>
          {opt}
        </MenuItem>
      ))}
    </Select>
  );
};

// Dependent dropdown edit cell — options come from a status-keyed map (Reason
// or Time Category). Always includes the row's current value even if it's a
// legacy value missing from the map, so the existing selection stays visible.
const DependentDropdownEditCell = (
  params: GridRenderEditCellParams & {
    optionMap: Record<string, string[]>;
  }
) => {
  const { row, optionMap, ...rest } = params;
  const mapped = row.status ? optionMap[row.status.toLowerCase()] ?? [] : [];
  const current = rest.value;
  const options =
    current && !mapped.includes(current)
      ? [String(current), ...mapped]
      : mapped;
  return (
    <DropdownEditCell {...rest} row={row} options={options} allowNone={false} />
  );
};

const downloadCsv = (rows: any[], filename: string, tz: string) => {
  const headers = [
    'Time',
    'Time End',
    'Duration (m)',
    'Equipment',
    'Status',
    'Reason',
    'Location',
    'Region',
    'Comment',
    'Source',
    'Time Category',
  ];
  const csvRows = [
    headers.join(','),
    ...rows.map((r) =>
      [
        r.timeStart ? dayjs(r.timeStart).tz(tz).format('DD/MM/YY HH:mm') : '',
        r.timeEnd ? dayjs(r.timeEnd).tz(tz).format('DD/MM/YY HH:mm') : '',
        r.durationMinutes != null ? Number(r.durationMinutes).toFixed(2) : '',
        r.equipment ?? '',
        r.status ?? '',
        r.reason ?? '',
        r.location ?? '',
        r.region ?? '',
        r.comment ?? '',
        r.byDispatcher ? 'Dispatcher' : 'Operator',
        r.timeCategory ?? '',
      ]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(',')
    ),
  ];
  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const EquipmentStatusReport = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  const gridApiRef = useGridApiRef();

  const customerId = useAppSelector(
    (state) => state.persistedReducer.customer.id
  );
  // Only used by computeShiftUTCWindow (temporarily disabled — see handleFetch).
  // const customerTimezone = useAppSelector(
  //   (state) => state.persistedReducer.customer.timezone ?? 'UTC'
  // );
  const shiftPeriods = useAppSelector(
    (state) => state.customerShiftConfigReducer.data?.shiftPeriods ?? []
  );

  const { timezoneBasedOnDisplayMode: tz } = useTimeDisplayMode();

  const canEdit = useRoleBasedAccess(EDITABLE_ROLES);

  const today = dayjs().format('YYYY-MM-DD');
  // 'single' = one date + shift, 'range' = date range, no shift
  const [mode, setMode] = useState<'single' | 'range'>('single');

  // single-date mode state
  const [date, setDate] = useState<string>(today);
  // One or more shifts may be selected; at least one is required to fetch.
  const [shifts, setShifts] = useState<string[]>([]);

  // range mode state
  const [rangeStart, setRangeStart] = useState<string>('');
  const [rangeEnd, setRangeEnd] = useState<string>('');

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  // Client-side filters — applied to already-fetched rows only
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [reasonFilter, setReasonFilter] = useState<string | null>(null);
  const [equipmentFilter, setEquipmentFilter] = useState<string | null>(null);
  const [timeCategoryFilter, setTimeCategoryFilter] = useState<string | null>(
    null
  );

  const assets = useAppSelector(selectAssets);

  // Status → reason-categories map. Fetched ONCE per mount (guarded ref so it
  // never refires on re-renders, report refetches, or filter changes).
  const [statusReasonList, setStatusReasonList] = useState<
    Array<{
      status: string;
      reasonCategories: string[];
      timeCategories: string[];
    }>
  >([]);
  const statusReasonsFetched = useRef(false);

  useEffect(() => {
    if (!customerId || statusReasonsFetched.current) return;
    statusReasonsFetched.current = true;
    const api = new ReportAPI();
    api
      .getEquipmentStatusReasons({ customerId })
      .then((data) => {
        setStatusReasonList(data ?? []);
      })
      .catch((err) => {
        // Allow a retry on next mount if the one-time fetch failed
        statusReasonsFetched.current = false;
        console.error('Failed to fetch status/reason options', err);
      });
  }, [customerId]);

  const statusOptionsForEdit = useMemo(
    () => statusReasonList.map((s) => s.status),
    [statusReasonList]
  );

  // Keyed by lowercased status so lookups are case-insensitive (row status may
  // be "READY" while the API returns "Ready").
  const statusReasonMap = useMemo(() => {
    const map: Record<string, string[]> = {};
    statusReasonList.forEach((s) => {
      map[s.status?.toLowerCase()] = s.reasonCategories ?? [];
    });
    return map;
  }, [statusReasonList]);

  // Status → time-categories map, same case-insensitive keying as reasons.
  const statusTimeCategoryMap = useMemo(() => {
    const map: Record<string, string[]> = {};
    statusReasonList.forEach((s) => {
      map[s.status?.toLowerCase()] = s.timeCategories ?? [];
    });
    return map;
  }, [statusReasonList]);

  // Customer locations — fetched ONCE per mount (same guarded pattern) for the
  // editable Location dropdown. LocationImporterAPI extends BaseAPI, whose
  // constructor calls hooks, so it must be created during render (useRef), not
  // inside the effect callback.
  const locationApi = useRef(new LocationImporterAPI()).current;
  const [locationList, setLocationList] = useState<string[]>([]);
  const locationsFetched = useRef(false);

  useEffect(() => {
    if (!customerId || locationsFetched.current) return;
    locationsFetched.current = true;
    locationApi
      .getLocations()
      .then((data) => {
        const types = Array.from(
          new Set((data ?? []).map((l) => l.locationType).filter(Boolean))
        ).sort((a, b) => a.localeCompare(b));
        setLocationList(types);
      })
      .catch((err) => {
        locationsFetched.current = false;
        console.error('Failed to fetch locations', err);
      });
  }, [customerId, locationApi]);

  // Last generated shift date — used to gate today in range mode
  const [lastGeneratedDate, setLastGeneratedDate] = useState<string | null>(
    null
  );

  const shiftOptions =
    shiftPeriods.length > 0
      ? shiftPeriods
      : [{ shiftName: 'Day Shift' }, { shiftName: 'Night Shift' }];

  const isShiftOngoing = useCallback((): boolean => {
    if (mode === 'range') return false;
    if (shifts.length === 0 || shiftPeriods.length === 0) return false;

    const now = dayjs();
    const selectedDate = dayjs(date);
    const isToday = selectedDate.isSame(now, 'day');
    if (!isToday) return false;

    const currentHour = now.hour();
    // Ongoing if ANY selected shift is currently in progress
    return shifts.some((shiftName) => {
      const period = shiftPeriods.find((s) => s.shiftName === shiftName);
      if (!period) return false;
      const { startHour, endHour } = period;
      if (endHour > startHour) {
        return currentHour >= startHour && currentHour < endHour;
      }
      return currentHour >= startHour || currentHour < endHour;
    });
  }, [date, shifts, shiftPeriods, mode]);

  const inputOutlineSx = {
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: palette.borderGrey },
      '&:hover fieldset': { borderColor: palette.borderGrey },
      '&.Mui-focused fieldset': {
        borderColor: palette.borderGrey,
        borderWidth: 1.5,
      },
      '& .MuiInputBase-input': { color: 'text.primary' },
      '& .MuiSvgIcon-root': { color: 'text.primary' },
    },
    '& .MuiInputLabel-root': {
      color: 'text.secondary',
      '&.Mui-focused': { color: 'text.primary' },
    },
    '& .MuiInputBase-root': { height: 40 },
  } as const;

  // Combined UTC window spanning all selected shifts (earliest start → latest end)
  // Temporarily disabled — see handleFetch. Sending shiftStart/shiftEnd was
  // crashing on malformed shift-hour config; backend filters by date + shift
  // name alone, so the window is not required. Kept commented for reference.
  // const computeShiftUTCWindow = (): {
  //   shiftStart: string;
  //   shiftEnd: string;
  // } | null => {
  //   const periods = shiftPeriods.filter((s) => shifts.includes(s.shiftName));
  //   if (periods.length === 0) return null;
  //
  //   let earliestStart: Dayjs | null = null;
  //   let latestEnd: Dayjs | null = null;
  //
  //   periods.forEach((period) => {
  //     const { startHour, endHour } = period;
  //     const start = dayjs.tz(
  //       `${date}T${String(startHour).padStart(2, '0')}:00:00`,
  //       customerTimezone
  //     );
  //     const endDate =
  //       endHour <= startHour
  //         ? dayjs(date).add(1, 'day').format('YYYY-MM-DD')
  //         : date;
  //     const end = dayjs.tz(
  //       `${endDate}T${String(endHour).padStart(2, '0')}:00:00`,
  //       customerTimezone
  //     );
  //     if (!earliestStart || start.isBefore(earliestStart))
  //       earliestStart = start;
  //     if (!latestEnd || end.isAfter(latestEnd)) latestEnd = end;
  //   });
  //
  //   if (!earliestStart || !latestEnd) return null;
  //   return {
  //     shiftStart: (earliestStart as Dayjs).utc().toISOString(),
  //     shiftEnd: (latestEnd as Dayjs).utc().toISOString(),
  //   };
  // };

  const handleFetch = async () => {
    if (!customerId) return;
    if (mode === 'single' && (!date || shifts.length === 0)) return;
    if (mode === 'range' && (!rangeStart || !rangeEnd || shifts.length === 0))
      return;

    setLoading(true);
    try {
      const api = new ReportAPI();
      let data: any[];

      if (mode === 'single') {
        // shiftStart/shiftEnd temporarily not sent — backend filters by
        // customerId + date + shift name, which is sufficient. Building the
        // UTC window from shift config was crashing on malformed shift hours.
        // const window = computeShiftUTCWindow();
        data = (await api.getEquipmentStatusReport({
          customerId,
          date,
          shift: shifts,
          shiftStart: null,
          shiftEnd: null,
        })) as any[];
      } else {
        data = (await api.getEquipmentStatusReport({
          customerId,
          dateFrom: rangeStart,
          dateTo: rangeEnd,
          shift: shifts,
          shiftStart: null,
          shiftEnd: null,
        })) as any[];
      }

      setRows(data.map((r: any, i: number) => ({ ...r, _gridId: r.id ?? i })));
      setStatusFilter(null);
      setReasonFilter(null);
      setEquipmentFilter(null);
      setTimeCategoryFilter(null);

      // Track the latest date present in returned data to gate today in range picker
      if (data.length > 0) {
        const latest = data.reduce((max: string, r: any) => {
          const d =
            r.date ??
            (r.timeStart ? dayjs(r.timeStart).format('YYYY-MM-DD') : '');
          return d > max ? d : max;
        }, '');
        if (latest) setLastGeneratedDate(latest);
      }

      setFetched(true);
    } catch (err) {
      console.error('Failed to fetch equipment status report', err);
    } finally {
      setLoading(false);
    }
  };

  const processRowUpdate = async (
    newRow: GridRowModel,
    oldRow: GridRowModel
  ) => {
    if (!newRow.id) return newRow;

    // Only touch reason / time category when the STATUS actually changed.
    // Editing other cells (location, comment, etc.) must never wipe them —
    // including legacy values that aren't in the status-codes table.
    let reason = newRow.reason;
    let timeCategory = newRow.timeCategory;
    const statusChanged = newRow.status !== oldRow.status;
    if (statusChanged) {
      const statusKey = newRow.status?.toLowerCase();
      const validReasons = statusKey ? statusReasonMap[statusKey] ?? [] : [];
      const validTimeCategories = statusKey
        ? statusTimeCategoryMap[statusKey] ?? []
        : [];
      // Clear only if the current value isn't valid for the new status
      reason =
        newRow.reason && validReasons.includes(newRow.reason)
          ? newRow.reason
          : null;
      timeCategory =
        newRow.timeCategory && validTimeCategories.includes(newRow.timeCategory)
          ? newRow.timeCategory
          : null;
    }
    const nextRow: GridRowModel = { ...newRow, reason, timeCategory };

    const api = new ReportAPI();
    await api.updateEquipmentStatusRow({
      id: nextRow.id,
      customerId,
      status: nextRow.status,
      reason: nextRow.reason,
      location: nextRow.location,
      region: nextRow.region,
      comment: nextRow.comment,
      byDispatcher: nextRow.byDispatcher,
      timeCategory: nextRow.timeCategory,
    });
    setRows((prev) =>
      prev.map((r) => (r.id === nextRow.id ? { ...r, ...nextRow } : r))
    );
    return nextRow;
  };

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const handleAddRow = async (payload: NewRowPayload) => {
    try {
      const api = new ReportAPI();
      await api.createEquipmentStatusRow({ customerId, ...payload });
      dispatch(
        showNotification({
          message: t('schedule_report_row_added'),
          type: 'success',
        })
      );
      // Refresh so the new server-computed row (with id, duration, etc.) shows
      await handleFetch();
    } catch (err) {
      console.error('Failed to add equipment status row', err);
      dispatch(
        showNotification({
          message: t('schedule_report_row_add_failed'),
          type: 'error',
        })
      );
      throw err; // keep the modal open on failure
    }
  };

  const handleDeleteRow = async (row: any) => {
    if (!row.id) return;
    setDeletingId(row.id);
    try {
      const api = new ReportAPI();
      await api.deleteEquipmentStatusRow({ id: row.id, customerId });
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      dispatch(
        showNotification({
          message: t('schedule_report_row_deleted'),
          type: 'success',
        })
      );
    } catch (err) {
      console.error('Failed to delete equipment status row', err);
      dispatch(
        showNotification({
          message: t('schedule_report_row_delete_failed'),
          type: 'error',
        })
      );
    } finally {
      setDeletingId(null);
    }
  };

  // Patch a single time field (Time / Time End) with an ISO 8601 UTC string,
  // then update the local row optimistically.
  const saveTime = async (
    row: any,
    field: 'timeStart' | 'timeEnd',
    iso: string
  ) => {
    if (!row.id) return;
    const api = new ReportAPI();
    await api.updateEquipmentStatusRow({
      id: row.id,
      customerId,
      [field]: iso,
    });
    setRows((prev) =>
      prev.map((r) => (r.id === row.id ? { ...r, [field]: iso } : r))
    );
  };

  // In range mode, today is only selectable if data has been generated for it
  const isRangeDayDisabled = (day: Dayjs): boolean => {
    const dayStr = day.format('YYYY-MM-DD');
    const monthAgo = dayjs().subtract(1, 'month').startOf('day');
    if (day.isBefore(monthAgo)) return true;
    if (day.isAfter(dayjs())) return true;
    // Disable today unless we've confirmed data exists for it
    if (dayStr === today && lastGeneratedDate !== today) return true;
    return false;
  };

  const columns: GridColDef[] = [
    {
      field: 'timeStart',
      headerName: 'Time',
      width: 150,
      renderCell: ({ row, value }) => (
        <EditableTimeCell
          value={value as string | null}
          editable={canEdit}
          tz={tz}
          maxDateTime={row.timeEnd ? dayjs(row.timeEnd).tz(tz) : undefined}
          onSave={async (iso) => {
            await saveTime(row, 'timeStart', iso);
          }}
        />
      ),
    },
    {
      field: 'timeEnd',
      headerName: 'Time End',
      width: 150,
      renderCell: ({ row, value }) => (
        <EditableTimeCell
          value={value as string | null}
          editable={canEdit}
          tz={tz}
          minDateTime={row.timeStart ? dayjs(row.timeStart).tz(tz) : undefined}
          onSave={async (iso) => {
            await saveTime(row, 'timeEnd', iso);
          }}
        />
      ),
    },
    {
      field: 'durationMinutes',
      headerName: 'Duration (m)',
      width: 110,
      type: 'number',
      valueFormatter: ({ value }: { value: unknown }) =>
        value != null ? Number(value).toFixed(2) : '',
    },
    { field: 'equipment', headerName: 'Equipment', width: 110 },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      editable: canEdit,
      renderCell: ({ value }) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            gap: 0.5,
          }}
        >
          {canEdit && (
            <ArrowDropDownIcon
              sx={{ fontSize: 18, opacity: 0.6, flexShrink: 0 }}
            />
          )}
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {value}
          </span>
        </Box>
      ),
      renderEditCell: (params) => (
        <DropdownEditCell
          {...params}
          options={statusOptionsForEdit}
          allowNone={false}
        />
      ),
    },
    {
      field: 'reason',
      headerName: 'Reason',
      width: 220,
      editable: canEdit,
      renderCell: ({ value }) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            gap: 0.5,
          }}
        >
          {canEdit && (
            <ArrowDropDownIcon
              sx={{ fontSize: 18, opacity: 0.6, flexShrink: 0 }}
            />
          )}
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {value}
          </span>
        </Box>
      ),
      renderEditCell: (params) => (
        <DependentDropdownEditCell {...params} optionMap={statusReasonMap} />
      ),
    },
    {
      field: 'location',
      headerName: 'Location',
      width: 140,
      editable: canEdit,
      renderCell: ({ value }) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            gap: 0.5,
          }}
        >
          {canEdit && (
            <ArrowDropDownIcon
              sx={{ fontSize: 18, opacity: 0.6, flexShrink: 0 }}
            />
          )}
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {value}
          </span>
        </Box>
      ),
      renderEditCell: (params) => (
        <DropdownEditCell {...params} options={locationList} />
      ),
    },
    {
      field: 'region',
      headerName: 'Region',
      width: 100,
      editable: canEdit,
      renderCell: ({ value }) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            gap: 0.5,
          }}
        >
          {canEdit && (
            <EditIcon sx={{ fontSize: 13, opacity: 0.4, flexShrink: 0 }} />
          )}
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {value}
          </span>
        </Box>
      ),
    },
    {
      field: 'comment',
      headerName: 'Comment',
      width: 200,
      editable: canEdit,
      renderCell: ({ value }) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            gap: 0.5,
          }}
        >
          {canEdit && (
            <EditIcon sx={{ fontSize: 13, opacity: 0.4, flexShrink: 0 }} />
          )}
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {value}
          </span>
        </Box>
      ),
    },
    {
      field: 'byDispatcher',
      headerName: 'Source',
      width: 120,
      renderCell: ({ value }) => (
        <span>{value ? 'Dispatcher' : 'Operator'}</span>
      ),
    },
    {
      field: 'timeCategory',
      headerName: 'Time Category',
      width: 230,
      editable: canEdit,
      renderCell: ({ value }) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            gap: 0.5,
          }}
        >
          {canEdit && (
            <ArrowDropDownIcon
              sx={{ fontSize: 18, opacity: 0.6, flexShrink: 0 }}
            />
          )}
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {value ?? ''}
          </span>
        </Box>
      ),
      renderEditCell: (params) => (
        <DependentDropdownEditCell
          {...params}
          optionMap={statusTimeCategoryMap}
        />
      ),
    },
    {
      field: '_delete',
      headerName: 'Action',
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'center',
      renderCell: ({ row }) =>
        canEdit ? (
          <IconButton
            size="small"
            color="error"
            disabled={deletingId === row.id}
            onClick={() => {
              void handleDeleteRow(row);
            }}
          >
            {deletingId === row.id ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        ) : null,
    },
  ];

  // Status filter options — same source as the editable Status dropdown
  // (the fetched status → reason map), not just distinct values in the data.
  const statusOptions = statusOptionsForEdit;

  // Reason filter options — the reasons for the currently selected status;
  // if no status is selected, all reasons across every status (so the filter
  // still works on its own and composes with the others).
  const reasonFilterOptions = useMemo(() => {
    if (statusFilter) {
      return [...(statusReasonMap[statusFilter.toLowerCase()] ?? [])].sort(
        (a, b) => a.localeCompare(b)
      );
    }
    const set = new Set<string>();
    statusReasonList.forEach((s) => {
      s.reasonCategories?.forEach((rc) => set.add(rc));
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [statusFilter, statusReasonMap, statusReasonList]);

  // Equipment options — all assets for the logged-in customer (bumper numbers,
  // e.g. "HR2979"), from the Redux asset slice.
  const equipmentOptions = useMemo(() => {
    const set = new Set<string>();
    assets.forEach((a) => {
      if (a.bumperNumber) set.add(a.bumperNumber);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [assets]);

  // Rows shown in the grid / exported to CSV after applying all filters.
  // The filters compose independently — order of selection doesn't matter,
  // and clearing one keeps the others applied.
  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      const statusOk =
        !statusFilter || r.status?.toLowerCase() === statusFilter.toLowerCase();
      const reasonOk =
        !reasonFilter ||
        (r.reason?.toLowerCase().includes(reasonFilter.toLowerCase()) ?? false);
      const equipmentOk =
        !equipmentFilter ||
        r.equipment?.toLowerCase() === equipmentFilter.toLowerCase();
      const timeCategoryOk =
        !timeCategoryFilter ||
        r.timeCategory?.toLowerCase() === timeCategoryFilter.toLowerCase();
      return statusOk && reasonOk && equipmentOk && timeCategoryOk;
    });
  }, [rows, statusFilter, reasonFilter, equipmentFilter, timeCategoryFilter]);

  const ongoing = isShiftOngoing();

  const canFetch =
    mode === 'single'
      ? Boolean(date && shifts.length > 0 && !loading)
      : Boolean(rangeStart && rangeEnd && shifts.length > 0 && !loading);

  const csvFilename =
    mode === 'single'
      ? `equipment-status-${date}-${shifts
          .map((s) => s.replace(/\s*shift\s*/i, '').trim())
          .join('-')} Shift.csv`
      : `equipment-status-${rangeStart}-to-${rangeEnd}.csv`;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        bgcolor: 'background.paper',
        width: { xs: '100%', md: '80%' },
        flexBasis: { md: '80%' },
        flexGrow: 1,
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 600, color: 'text.primary', mb: 2.5 }}
      >
        Equipment Status Report
      </Typography>

      {/* Mode toggle */}
      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={mode}
          exclusive
          size="small"
          onChange={(_, val) => {
            if (!val) return;
            setMode(val);
            setFetched(false);
            setRows([]);
            setStatusFilter(null);
            setReasonFilter(null);
            setEquipmentFilter(null);
            setTimeCategoryFilter(null);
          }}
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              px: 2,
              fontSize: '0.8rem',
              color: 'text.secondary',
              borderColor: palette.borderGrey,
              '&.Mui-selected': {
                color: 'text.primary',
                bgcolor: enableDarkTheme
                  ? 'rgba(255,255,255,0.08)'
                  : 'rgba(0,0,0,0.06)',
              },
            },
          }}
        >
          <ToggleButton value="single">Single Date</ToggleButton>
          <ToggleButton value="range">Date Range</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Stack
        direction="row"
        alignItems="flex-end"
        sx={{ mb: 3, flexWrap: 'wrap', gap: 2 }}
      >
        {mode === 'single' ? (
          <>
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
              >
                Date
              </Typography>
              <DatePicker
                value={date ? dayjs(date) : null}
                maxDate={dayjs()}
                onChange={(v) => {
                  setDate(v?.format('YYYY-MM-DD') ?? '');
                  setFetched(false);
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    inputProps: { readOnly: true },
                    sx: { ...inputOutlineSx, width: 180 },
                  },
                }}
              />
            </Box>

            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
              >
                Shift
              </Typography>
              <FormControl size="small" sx={{ ...inputOutlineSx, width: 200 }}>
                <Select
                  multiple
                  value={shifts}
                  displayEmpty
                  onChange={(e) => {
                    const val = e.target.value;
                    setShifts(typeof val === 'string' ? val.split(',') : val);
                    setFetched(false);
                  }}
                  renderValue={(selected) =>
                    selected.length === 0 ? (
                      <span style={{ color: '#aaa' }}>Select shift(s)</span>
                    ) : (
                      selected.join(', ')
                    )
                  }
                >
                  {shiftOptions.map((s) => (
                    <MenuItem key={s.shiftName} value={s.shiftName}>
                      <Checkbox checked={shifts.includes(s.shiftName)} />
                      <ListItemText primary={s.shiftName} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </>
        ) : (
          <>
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
              >
                From
              </Typography>
              <DatePicker
                value={rangeStart ? dayjs(rangeStart) : null}
                maxDate={rangeEnd ? dayjs(rangeEnd) : dayjs()}
                minDate={dayjs().subtract(1, 'month').startOf('day')}
                shouldDisableDate={isRangeDayDisabled}
                onChange={(v) => {
                  setRangeStart(v?.format('YYYY-MM-DD') ?? '');
                  setFetched(false);
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    inputProps: { readOnly: true },
                    sx: { ...inputOutlineSx, width: 160 },
                  },
                }}
              />
            </Box>

            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
              >
                To
              </Typography>
              <DatePicker
                value={rangeEnd ? dayjs(rangeEnd) : null}
                minDate={
                  rangeStart
                    ? dayjs(rangeStart)
                    : dayjs().subtract(1, 'month').startOf('day')
                }
                maxDate={
                  rangeStart
                    ? dayjs(rangeStart).add(1, 'month').isAfter(dayjs())
                      ? dayjs()
                      : dayjs(rangeStart).add(1, 'month')
                    : dayjs()
                }
                shouldDisableDate={isRangeDayDisabled}
                onChange={(v) => {
                  setRangeEnd(v?.format('YYYY-MM-DD') ?? '');
                  setFetched(false);
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    inputProps: { readOnly: true },
                    sx: { ...inputOutlineSx, width: 160 },
                  },
                }}
              />
            </Box>

            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
              >
                Shift
              </Typography>
              <FormControl size="small" sx={{ ...inputOutlineSx, width: 200 }}>
                <Select
                  multiple
                  value={shifts}
                  displayEmpty
                  onChange={(e) => {
                    const val = e.target.value;
                    setShifts(typeof val === 'string' ? val.split(',') : val);
                    setFetched(false);
                  }}
                  renderValue={(selected) =>
                    selected.length === 0 ? (
                      <span style={{ color: '#aaa' }}>Select shift(s)</span>
                    ) : (
                      selected.join(', ')
                    )
                  }
                >
                  {shiftOptions.map((s) => (
                    <MenuItem key={s.shiftName} value={s.shiftName}>
                      <Checkbox checked={shifts.includes(s.shiftName)} />
                      <ListItemText primary={s.shiftName} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </>
        )}

        <Button
          variant="contained"
          onClick={handleFetch}
          disabled={!canFetch}
          sx={{ textTransform: 'none', height: 40 }}
        >
          {loading ? <CircularProgress size={18} color="inherit" /> : 'Fetch'}
        </Button>

        <Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
          >
            Status
          </Typography>
          <Autocomplete
            size="small"
            disabled={!fetched || statusOptions.length === 0}
            options={statusOptions}
            value={statusFilter}
            onChange={(_, val) => {
              setStatusFilter(val);
              // Reason depends on status — clear a now-inapplicable reason
              setReasonFilter(null);
            }}
            sx={{ width: 200 }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="All statuses"
                sx={inputOutlineSx}
              />
            )}
          />
        </Box>

        <Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
          >
            Reason
          </Typography>
          <Autocomplete
            size="small"
            disabled={!fetched || reasonFilterOptions.length === 0}
            options={reasonFilterOptions}
            value={reasonFilter}
            onChange={(_, val) => {
              setReasonFilter(val);
            }}
            sx={{ width: 200 }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="All reasons"
                sx={inputOutlineSx}
              />
            )}
          />
        </Box>

        <Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
          >
            Equipment
          </Typography>
          <Autocomplete
            size="small"
            disabled={!fetched}
            options={equipmentOptions}
            value={equipmentFilter}
            onChange={(_, val) => {
              setEquipmentFilter(val);
            }}
            sx={{ width: 200 }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="All equipment"
                sx={inputOutlineSx}
              />
            )}
          />
        </Box>

        <Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
          >
            Time Category
          </Typography>
          <Autocomplete
            size="small"
            disabled={!fetched}
            options={TIME_CATEGORY_OPTIONS}
            value={timeCategoryFilter}
            onChange={(_, val) => {
              setTimeCategoryFilter(val);
            }}
            sx={{ width: 200 }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="All time categories"
                sx={inputOutlineSx}
              />
            )}
          />
        </Box>
      </Stack>

      {fetched && (
        <>
          {canEdit && (
            <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  setAddOpen(true);
                }}
                sx={{
                  textTransform: 'none',
                  borderColor: palette.borderGrey,
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: palette.borderGrey,
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                Add Row
              </Button>
            </Stack>
          )}

          <Box sx={{ height: 480, width: '100%' }}>
            <DataGrid
              apiRef={gridApiRef}
              rows={filteredRows}
              columns={columns}
              getRowId={(r) => r._gridId ?? r.id}
              onCellClick={(params: GridCellParams) => {
                // Single click enters edit mode for editable cells, so the
                // Status/Reason dropdowns open on the first click on the cell
                // or its arrow instead of requiring a double-click.
                if (
                  params.isEditable &&
                  gridApiRef.current.getCellMode(params.id, params.field) ===
                    'view'
                ) {
                  gridApiRef.current.startCellEditMode({
                    id: params.id,
                    field: params.field,
                  });
                }
              }}
              processRowUpdate={processRowUpdate}
              onProcessRowUpdateError={(err) => {
                console.error('Row update failed:', err);
              }}
              pageSizeOptions={[25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
              }}
              sx={{
                border: `1px solid ${palette.borderGrey}`,
                '& .MuiDataGrid-columnHeaders': {
                  bgcolor: 'background.default',
                },
              }}
            />
          </Box>

          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              disabled={ongoing || filteredRows.length === 0}
              onClick={() => {
                downloadCsv(filteredRows, csvFilename, tz);
              }}
              title={
                ongoing
                  ? 'Shift is still ongoing — download available after shift ends'
                  : ''
              }
              sx={{
                textTransform: 'none',
                borderColor: palette.borderGrey,
                color: 'text.primary',
                '&:hover': {
                  borderColor: palette.borderGrey,
                  backgroundColor: 'action.hover',
                },
              }}
            >
              {ongoing ? 'Shift ongoing — Download disabled' : 'Download CSV'}
            </Button>
          </Stack>
        </>
      )}

      <AddEquipmentStatusRowModal
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
        }}
        onSubmit={handleAddRow}
        shiftOptions={shiftOptions.map((s) => s.shiftName)}
        equipmentOptions={equipmentOptions}
        statusOptions={statusOptionsForEdit}
        statusReasonMap={statusReasonMap}
        statusTimeCategoryMap={statusTimeCategoryMap}
        locationOptions={locationList}
        inputOutlineSx={inputOutlineSx}
      />
    </Paper>
  );
};

export default EquipmentStatusReport;
